import styled from '@emotion/styled';
import { forwardRef } from 'react';

import Icon from 'storybookConfig/stories/molecules/Icon';
import Input, { INPUT_HEIGHT } from 'storybookConfig/stories/molecules/Input';

const ICON_CONTAINER_SIZE = INPUT_HEIGHT;

const StyledInput = styled(Input)`
  padding-right: ${ICON_CONTAINER_SIZE - 6}px;

  &::-webkit-calendar-picker-indicator {
    opacity: 0;
    position: absolute;
    right: 14px;
  }
`;

type WrapperProps = Pick<DateInputProps, 'disabled' | 'isFullWidth'>;

const Wrapper = styled.div<WrapperProps>`
  display: ${({ isFullWidth }) => (isFullWidth ? 'block' : 'inline-block')};
  position: relative;
  overflow: hidden;
  cursor: ${({ disabled }) => disabled && 'not-allowed'};
`;

type DateIconProps = Pick<DateInputProps, 'disabled'>;

const StyledIcon = styled(Icon)<DateIconProps>`
  pointer-events: none;
  position: absolute;
  inset: 0 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${ICON_CONTAINER_SIZE}px;
  height: ${ICON_CONTAINER_SIZE}px;
`;

interface DateInputProps extends React.ComponentProps<typeof Input> {}

/**
 * Extends an `Input` component's styling and wraps it to provide iconography. Props
 * passed to `DateInput` will be curried to the underlying `Input` component.
 */

const DateInput = forwardRef<HTMLInputElement, DateInputProps>(
  ({ disabled, isFullWidth, ...inputProps }, ref) => (
    <Wrapper isFullWidth={isFullWidth} disabled={disabled}>
      <StyledInput
        type="datetime-local"
        disabled={disabled}
        isFullWidth={isFullWidth}
        ref={ref}
        {...inputProps}
      />
      <StyledIcon
        name="calendar_month"
        disabled={disabled}
        color={disabled ? 'bodyTextDisabled' : 'iconDefault'}
      />
    </Wrapper>
  )
);

DateInput.displayName = 'DateInput';

export default DateInput;
